import { useCallback } from 'react';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleMenu from 'components/SimpleMenu';
import { SearchTeamsByFieldsPayload, TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';
import useExportToExcel from 'hooks/useExportToExcel';
import { GetTeamsForExportResponse } from 'TeamModule/services/GetTeamsForExportApi';
import { useTeamsForExportMutation } from 'TeamModule/query/hooks/useTeamsForExportMutation';
import { useRefreshTeamIntegrationsMutation } from 'TeamModule/query/hooks/useRefreshTeamIntegrationsMutation';

export const useTeamsBulkActionMenu = () => {
  const { openModal } = useDynamicModal();
  const { exportToExcel } = useExportToExcel();
  const { exportTeams } = useTeamsForExportMutation();
  const { refreshTeamIntegrations, isRefreshingTeamIntegrations } =
    useRefreshTeamIntegrationsMutation();

  const openTeamsBulkActionMenu = useCallback(
    ({
      target,
      onDeactivate,
      onScheduleDeactivate,
      onRefreshIntegrations,
      filtersPayload,
      shouldDisableTeamDeactivation,
      shouldDisableTeamScheduleDeactivation,
      shouldDisableRefreshIntegrations,
      exportFileName,
    }: {
      target: HTMLElement;
      onDeactivate: VoidFunction;
      onScheduleDeactivate: VoidFunction;
      onRefreshIntegrations: VoidFunction;
      filtersPayload: SearchTeamsByFieldsPayload;
      shouldDisableTeamDeactivation: boolean;
      shouldDisableTeamScheduleDeactivation: boolean;
      shouldDisableRefreshIntegrations: boolean;
      exportFileName?: string;
    }) => {
      openModal({
        modal: ({ toggle }) => (
          <SimpleMenu
            anchorEl={target}
            items={[
              {
                label: 'Export All Teams to Excel',
                onClick: async () => {
                  const fileName = exportFileName || 'teams';
                  const {
                    sortBy = 'lastBackfilled',
                    sortOrder = 'DESC',
                    tenant,
                    realmId,
                    teamId,
                    csm,
                    teamIds,
                    teamIdsNot,
                    tags,
                    integrations,
                    ssoEnabled,
                    openApiEnabled,
                    ddiEnabled,
                    internalTestTeam,
                    chelloCompliant,
                    aiEnabled,
                    aiBlocked,
                    personal,
                    discarded,
                  } = filtersPayload || {};

                  // Build query params, filtering out null/undefined and empty arrays
                  const baseParams = {
                    all: true,
                    sortBy,
                    sortOrder,
                    tenant,
                    realmId,
                    teamId,
                    csm,
                    teamIds,
                    teamIdsNot,
                    tags,
                    integrations,
                    ssoEnabled,
                    openApiEnabled,
                    ddiEnabled,
                    internalTestTeam,
                    chelloCompliant,
                    aiEnabled,
                    aiBlocked,
                    personal,
                    discarded,
                  };

                  // Filter out null/undefined and empty arrays while preserving types
                  const q = Object.entries(baseParams).reduce(
                    (acc, [key, value]) => {
                      // Keep booleans as-is, non-empty arrays, and truthy non-array values
                      if (
                        typeof value === 'boolean' ||
                        (Array.isArray(value) && value.length > 0) ||
                        (!Array.isArray(value) && value != null && value !== '')
                      ) {
                        acc[key] = value;
                      }
                      return acc;
                    },
                    {} as Record<string, any>,
                  ) as SearchTeamsByFieldsPayload;

                  exportTeams(q, {
                    onSuccess: (result) => {
                      const response = result?.data as GetTeamsForExportResponse | undefined;
                      const rows: TeamSearchItem[] = (response?.data as TeamSearchItem[]) || [];
                      const formatted = rows.map((team: TeamSearchItem) => ({
                        ...team,
                        tags: Array.isArray(team.tags) ? team.tags.join(', ') : team.tags || '',
                      }));
                      exportToExcel({ data: formatted, fileName });
                    },
                  });
                },
              },
              {
                label: 'Deactivate Teams',
                onClick: () => onDeactivate(),
                disableButton: shouldDisableTeamDeactivation,
              },
              {
                label: 'Schedule Deactivate Teams',
                onClick: () => onScheduleDeactivate(),
                disableButton: shouldDisableTeamScheduleDeactivation,
              },
              {
                label: 'Refresh Integrations Data',
                onClick: () => onRefreshIntegrations(),
                disableButton: shouldDisableRefreshIntegrations,
              },
            ]}
            handleClose={toggle}
          />
        ),
      });
    },
    [exportTeams, exportToExcel, openModal, refreshTeamIntegrations],
  );

  return {
    openTeamsBulkActionMenu,
    refreshTeamIntegrations,
    isRefreshingTeamIntegrations,
  };
};
